import React, { useEffect, useMemo, useState } from "react";

import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Grid,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { endOfMonth } from "date-fns";
import LineChartComponent from "../../components/charts/LineChart";
import { Switch } from "@mui/material";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
	FetchDataFailureAction,
	FetchDataSuccessAction,
	fetchData,
	postData,
} from "../../store/actions";

import {
	addAttendanceRecord,
	listAttendanceRecords,
	viewActiveUsers,
} from "../../Service/Apis";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import {
	ToastVariant,
	useCustomToast,
} from "../../components/Methods/SnackBar";
import NumberInput from "../../components/NumberInput/NumberInput";
import {
	AttendanceResponseType,
	AttendanceSummaryType,
	AttendanceType,
} from "./Types/AttendaceTypes";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import { startOfMonth } from "date-fns";
import Loading from "../../components/Loading/Loading";
import { PAGINATION_CONFIG, getPageSizeOptionsForDataGrid } from "utils/paginationConfig";

interface Employeetype {
	id: string;
	employee_code: string;
	first_name: string;
	last_name: string;
	gender: string;
	roles: [];
	designation: string;
	date_of_birth: string;
	date_of_joining: string;
	date_of_confirmation: string;
	date_of_exit: string;
	email: string;
	password: string;
}

interface PaginationState {
	page: number;
	pageSize: number;
	total: number;
}

/**
 * ReviewAttendance component for managing and displaying employee attendance records.
 *
 * @component
 * @description
 * This component provides functionality to:
 * - Select employees from a dropdown
 * - Filter attendance records by date range
 * - Display attendance data in a paginated data grid
 * - Show attendance summary statistics
 * - Handle attendance record updates
 *
 * @features
 * - Employee selection via Autocomplete
 * - Date range filtering with DatePicker
 * - Server-side pagination
 * - Attendance data grid with sortable columns
 * - Summary statistics display
 * - Loading states management
 *
 * @state
 * - employeesData: Array of employee data
 * - employee: Currently selected employee
 * - fromDate: Start date for filtering
 * - toDate: End date for filtering
 * - attendance: Current attendance records and summary
 * - edit: Edit mode state
 * - pagination: Pagination state (page, pageSize, total)
 *
 * @hooks
 * - useDispatch: For Redux actions dispatch
 * - useSelector: For accessing loading state
 * - useCustomToast: For displaying notifications
 * - useEffect: For data fetching and side effects
 *
 * @returns {JSX.Element} A React component for reviewing and managing attendance records
 */
const ReviewAttendance = () => {
	// State Hooks
	const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
	const [employee, setEmployee] = useState<Employeetype | null>(null);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [fromDate, setFromDate] = React.useState<Dayjs | null>(null);
	const [toDate, setToDate] = React.useState<Dayjs | null>(null);
	const [attendance, setAttendance] = useState<AttendanceResponseType>({
		results: [],
		summary: {} as AttendanceSummaryType,
	});
	const [edit, setEdit] = useState<Boolean>(false);
	const [forceUpdateKey, setForceUpdateKey] = useState(0);
	const [pagination, setPagination] = useState<PaginationState>({
		page: 1,
		pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
		total: 0,
	});
	// Redux Hooks
	const dispatch = useDispatch();
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();

	const showToast = useCustomToast();

	const isLoading = useSelector((state: any) => state.loading.isLoading);

	const handleShowToast = (
		message: string,
		variant: ToastVariant,
		isCloseable: boolean
	) => {
		showToast(message, variant, isCloseable);
	};
	//Handle Employee Change Function
	const handleEmployeeChange = (e: any, values: any) => {
		setEmployee(values);
	};

	const handleNumberInputChange = (
		e: any,
		newValue: number | null,
		propertyName: keyof AttendanceType
	) => {
		e.preventDefault();
		const roundedValue = newValue ? parseFloat(newValue?.toFixed(1)) : 0;
		setAttendance((prev) => ({
			...prev,
			results: prev.results.map((item, index) =>
				index === 0 ? { ...item, [propertyName]: roundedValue } : item
			),
		}));
		setForceUpdateKey(forceUpdateKey + 1);
	};

	const handleSave = async (e: any) => {
		e.preventDefault();
		console.log(attendance);

		try {
			dispatch(setIsLoading(true));
			const response = await dispatch2(
				postData(`${addAttendanceRecord}`, attendance.results[0])
			);
			handleShowToast(
				"Attendance record added successfully !",
				"success",
				true
			);
			setEmployee(null);
		} catch (error) {
			console.log(error);
		} finally {
			dispatch(setIsLoading(false));
		}
	};
	// Add new type for chart metrics
	type ChartMetric = {
		value: string;
		label: string;
		accessor: keyof (typeof attendance.results)[0];
	};

	// Add new state for selected metric
	const [selectedMetric, setSelectedMetric] = useState<ChartMetric>({
		value: "late_marks",
		label: "Late Marks",
		accessor: "late_marks",
	});

	// Add array of available metrics
	const chartMetrics: ChartMetric[] = [
		{ value: "late_marks", label: "Late Marks", accessor: "late_marks" },
		{
			value: "overtime_hours",
			label: "Overtime Hours",
			accessor: "overtime_hours",
		},
		{
			value: "regularized_days",
			label: "Regularized Days",
			accessor: "regularized_days",
		},
	];

	// Update transformDataForChart function
	const transformDataForChart = (data: any[]) => {
		return data.map((item) => ({
			week: dayjs(item.week_dates.week_end).format("DD MMM YYYY"), // For x-axis display
			tooltip: {
				start: dayjs(item.week_dates.week_start).format("DD MMM YYYY"),
				end: dayjs(item.week_dates.week_end).format("DD MMM YYYY"),
			},
			employee_name: item.employee_name,
			percent: item[selectedMetric.accessor],
			employee_id: item.user_id,
		}));
	};

	// Add handler for employee selection
	const handleEmployeeSelection = (employeeName: string) => {
		setSelectedEmployees((prev) => {
			if (prev.includes(employeeName)) {
				return prev.filter((name) => name !== employeeName);
			}
			return [...prev, employeeName];
		});
	};

	// Add handler for select all switch
	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);
		if (checked) {
			setSelectedEmployees(
				attendance.results.map((item) => item.employee_name)
			);
		} else {
			setSelectedEmployees([]);
		}
	};
	const rows = attendance.results.map((data, index) => ({
		...data,
		index: index,
		sr_no: index + 1,
		month_year: dayjs(data.date).format("MMM-YYYY"),
	}));

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: "sr_no",
				headerName: "Sr. No.",
				width: 90,
				align: "center",
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Sr. No.
					</strong>
				),
			},
			{
				field: "employee_name",
				headerName: "Employee Name",
				align: "center",
				width: 160,
				headerAlign: "center",
				headerClassName: "header-background",
				hide: true,
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Employee Name
					</strong>
				),
			},
			{
				field: "month_year",
				headerName: "Employee Name",
				align: "center",
				width: 120,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Month-Year
					</strong>
				),
			},

			{
				field: "late_marks",
				headerName: "Late Marks",
				align: "center",
				width: 110,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Late Marks
					</strong>
				),
			},
			{
				field: "overtime_hours",
				headerName: "Overtime Hours",
				align: "center",
				width: 140,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Overtime Hours
					</strong>
				),
			},
			{
				field: "regularized_days",
				headerName: "Regularized Days",
				align: "center",
				width: 140,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Regularized Days
					</strong>
				),
			},
			{
				field: "week_dates",
				headerName: "Week Dates",
				align: "center",
				width: 200,
				headerAlign: "center",
				headerClassName: "header-background",
			renderCell: (params) => (
				<span>
					{params?.value?.week_start ? dayjs(params.value.week_start).format("DD/MM/YYYY") : "-"}
					{" to "}
					{params?.value?.week_end ? dayjs(params.value.week_end).format("DD/MM/YYYY") : "-"}
				</span>
			),
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Week Dates
					</strong>
				),
			},
		];
	}, [rows]);

	const AttendanceSummary = ({ summary }: { summary: any }) => {
		// Helper function to safely format numbers
		const formatNumber = (
			value: number | null | undefined,
			decimals: number = 2
		): string => {
			if (value === null || value === undefined) return "0";
			return Number(value).toFixed(decimals);
		};

		return (
			<Box sx={{ mt: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
				<Typography variant="h6" gutterBottom>
					Summary
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="subtitle1">
							Total Late Marks: <Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(summary?.total_late_marks, 0)}</Box>
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="subtitle1">
							Total Overtime Hours: <Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(summary?.total_overtime)}</Box>
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="subtitle1">
							Total Regularized Days:{" "}
							<Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(summary?.total_regularized_days, 0)}</Box>
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="subtitle1">
							Average Late Marks: <Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(summary?.avg_late_marks)}</Box>
						</Typography>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="subtitle1">
							Average Overtime: <Box component="span" sx={{ fontWeight: 700 }}>{formatNumber(summary?.avg_overtime)}</Box>
						</Typography>
					</Grid>
					{summary?.week_start && summary?.week_end && (
						<Grid item xs={12}>
							<Typography variant="subtitle1">
								Week: <Box component="span" sx={{ fontWeight: 700 }}>{summary.week_start}</Box> to <Box component="span" sx={{ fontWeight: 700 }}>{summary.week_end}</Box>
							</Typography>
						</Grid>
					)}
				</Grid>
			</Box>
		);
	};

	// Add TypeScript interface if not already defined

	useEffect(() => {
		// API Call get the User Details.
		const fetchUserDetails = async () => {
			try {
				dispatch(setIsLoading(true));
				const response = await dispatch2(fetchData(`${viewActiveUsers}`));
				setEmployeesData(response as Employeetype[]);
			} catch (error) {
				console.log(error);
			} finally {
				dispatch(setIsLoading(false));
			}
		};

		fetchUserDetails();
	}, []);

	const fetchAttendance = async (page: number = 1) => {
		try {
			dispatch(setInPageLoading(true));
			const queryParams = new URLSearchParams();

			if (employee?.id) queryParams.append("user_id", employee.id);
			if (fromDate)
				queryParams.append("date_from", fromDate.format("YYYY-MM-DD"));
			if (toDate) queryParams.append("date_to", toDate.format("YYYY-MM-DD"));
			queryParams.append("page", page.toString());
			queryParams.append("page_size", pagination.pageSize.toString());

			const response = await dispatch2(
				fetchData(`${listAttendanceRecords}?${queryParams.toString()}`)
			);

			setAttendance({
				results: response.results,
				summary: response.summary,
			});
			setPagination((prev) => ({
				...prev,
				page,
				total: response.count,
			}));
		} catch (error) {
			console.error(error);
			handleShowToast("Error fetching attendance records", "error", true);
		} finally {
			dispatch(setInPageLoading(false));
		}
	};

	useEffect(() => {
		// API call to get the employee skill responses.
		if ((fromDate && toDate) || (!fromDate && !toDate)) {
			fetchAttendance(1);
		}
	}, [employee, fromDate, toDate, pagination.pageSize]);

	useEffect(() => {
		setEdit(false);
	}, [employee]);

	// Loading Spinner Component
	if (isLoading) {
		return (
			<>
				<Loading isLoading={isLoading as boolean} />
			</>
		);
	}

	// Main Component

	return (
		<>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
				}}
			>
				<Box>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Autocomplete
							size="small"
							isOptionEqualToValue={(option, value) => option.id === value.id}
							getOptionLabel={(option) =>
								option.first_name + " " + option.last_name
							}
							value={employee}
							onChange={handleEmployeeChange}
							id="employee-autocomplete"
							options={employeesData}
							sx={{ width: "40vw", mx: 2, px: 2 }}
							renderInput={(params) => <TextField {...params} />}
						/>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<DatePicker
									label="From"
									views={["month", "year"]}
									sx={{
										".MuiInputLabel-root": {
											transform: "translate(14px, 8px) scale(1)",
										},
										".MuiInputLabel-shrink": {
											transform: "translate(14px, -6px) scale(0.75)",
										},
										".MuiInputBase-root .MuiInputBase-input": {
											/* Your styles here */
											height: "0.55rem",
											width: "8rem",
										},
										mx: 1,
									}}
									value={fromDate}
									onChange={(newValue) =>
										setFromDate(
											newValue ? dayjs(startOfMonth(newValue.toDate())) : null
										)
									}
									disableFuture
								/>
								<DatePicker
									label="To"
									sx={{
										".MuiInputLabel-root": {
											transform: "translate(14px, 8px) scale(1)",
										},
										".MuiInputLabel-shrink": {
											transform: "translate(14px, -6px) scale(0.75)",
										},
										".MuiInputBase-root .MuiInputBase-input": {
											/* Your styles here */
											height: "0.55rem",
											width: "8rem",
										},
										mx: 1,
									}}
									views={["month", "year"]}
									value={toDate}
									onChange={(newValue) =>
										setToDate(
											newValue ? dayjs(endOfMonth(newValue.toDate())) : null
										)
									}
									disableFuture
								/>
								<Button
									size="small"
									disableElevation
									disableFocusRipple
									onClick={() => {
										setFromDate(null);
										setToDate(null);
									}}
								>
									Clear
								</Button>
							</Box>
						</LocalizationProvider>
					</Box>
					{
						//   <Typography
						//     id="employee-autocomplete-label"
						//
						//     variant="body2"
						//     style={{ marginTop: "16px" }}
						//     sx={{ display: "flex", justifyContent: "center" }}
						//   >
						//     Select an employee to view attendance.
						//   </Typography>
						// ) : (
						<Box sx={{ mt: 2, pt: 2 }}>
							{attendance.results.length === 0 ? (
								<>
									<Box
										sx={{
											mt: 12,
											left: "50%",
											// transform: "translate(-50%, -50%)",
											display: "flex",
											justifyContent: "center",
										}}
									>
										<NothingToDisplayComponent />
									</Box>
								</>
							) : (
								<>
									{attendance.summary && (
										<AttendanceSummary summary={attendance.summary} />
									)}
									<Box
										sx={{
											width: "100%",
											display: "flex",
											flexDirection: "column",
											m: 2,
											p: 2,
										}}
									>
										<Paper>
											<DataGrid
												autoHeight
												disableColumnMenu
												sx={{
													width: "100%",
													height: "100%",
													"--DataGrid-overlayHeight": "50vh",
												}}
												paginationMode="server"
												rowCount={pagination.total}
												initialState={{
													pagination: {
														paginationModel: {
															page: pagination.page - 1,
															pageSize: pagination.pageSize,
														},
													},
												}}
												onPaginationModelChange={(model) => {
													setPagination((prev) => ({
														...prev,
														pageSize: model.pageSize,
													}));
													fetchAttendance(model.page + 1);
												}}
												pageSizeOptions={getPageSizeOptionsForDataGrid()}
												rows={rows}
												columns={columns}
												disableColumnSelector
												disableDensitySelector
												slots={{
													toolbar: GridToolbar,
													noRowsOverlay: CustomNoRowsOverlay,
													noResultsOverlay: CustomNoRowsOverlay,
												}}
												slotProps={{
													toolbar: {
														// csvOptions: { disableToolbarButton: true },
														showQuickFilter: true,
														// printOptions: { disableToolbarButton: true },
														style: { margin: "0.5rem" },
													},
												}}
												getRowId={(row) => row.attendance_id}
											/>
										</Paper>
									</Box>
									{attendance.results.length > 0 && (
										<Box sx={{ mt: 4, p: 2, width: "89%" }}>
											<Paper sx={{ p: 2 }}>
												<Box
													display="flex"
													justifyContent="space-between"
													alignItems="center"
													mb={2}
												>
													<Box display="flex" alignItems="center" gap={2}>
														<Typography variant="h6">
															Weekly Attendance Chart
														</Typography>
														<TextField
															select
															size="small"
															value={selectedMetric.value}
															onChange={(e) => {
																const metric = chartMetrics.find(
																	(m) => m.value === e.target.value
																);
																if (metric) setSelectedMetric(metric);
															}}
															sx={{ minWidth: 150 }}
														>
															{chartMetrics.map((metric) => (
																<MenuItem
																	key={metric.value}
																	value={metric.value}
																>
																	{metric.label}
																</MenuItem>
															))}
														</TextField>
													</Box>
													<Box display="flex" alignItems="center" gap={1}>
														<Typography>Select All</Typography>
														<Switch
															checked={selectAll}
															onChange={handleSwitchChange}
															color="primary"
														/>
													</Box>
												</Box>
												<LineChartComponent
													data={transformDataForChart(attendance.results)}
													YAxisLabel={selectedMetric.label}
													selectedEmployees={selectedEmployees}
													onEmployeeSelection={handleEmployeeSelection}
												/>
											</Paper>
										</Box>
									)}
								</>
							)}
						</Box>
					}
				</Box>
			</Box>
		</>
	);
};

export default ReviewAttendance;
